import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FiArrowLeft,
  FiMail,
  FiCalendar,
  FiLock,
  FiEye,
  FiEyeOff,
  FiUser,
  FiCamera,
} from 'react-icons/fi';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../hooks/useAuth';
import { useUpdateProfileMutation } from '../../api/auth';
import { toast } from '../../utils/toast';
import { signupStore } from '../../utils/signupStore';
import { apiClient } from '../../api/api';
import { DateInput } from '../ui/DateInput';
import { FormLabel } from '../ui/FormLabel';
import { AuthHeader } from './AuthHeader';

const checkPasswordStrength = (pw: string) => ({
  isLengthValid: pw.length >= 8 && pw.length <= 20,
  hasUppercase: /[A-Z]/.test(pw),
  hasNumber: /\d/.test(pw),
  hasSpecialChar: /[!#$%&()*+,\-./:;<=>?@[\\\]^_`{|}~]/.test(pw),
});

export const PersonalInformationPage: React.FC = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const profileMutation = useUpdateProfileMutation();
  const isSignupFlow = signupStore.isActive();
  const signupDraft = isSignupFlow ? signupStore.get() : null;
  const isGoogleSignup = isSignupFlow && !!signupDraft?.googleSignedUp;
  const isAppleSignup = isSignupFlow && !!signupDraft?.appleSignedUp;
  const isSocialSignup = isGoogleSignup || isAppleSignup;

  const [formData, setFormData] = useState({
    fullName: '',
    dob: '',
    username: '',
    password: '',
    confirmPassword: '',
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Password strength – matches mobile checkPassword()
  const pwStrength = checkPasswordStrength(formData.password);
  const passwordIsValid =
    pwStrength.isLengthValid &&
    pwStrength.hasUppercase &&
    pwStrength.hasNumber &&
    pwStrength.hasSpecialChar;

  const [debouncedUsername, setDebouncedUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>(
    'idle',
  );
  const [usernameMsg, setUsernameMsg] = useState('');

  useEffect(() => {
    if (!isSignupFlow) return;

    const trimmed = formData.username.trim();
    if (!trimmed) {
      setUsernameStatus('idle');
      setUsernameMsg('');
      setDebouncedUsername('');
      return;
    }

    // Immediate feedback - "switching mindset" to loading
    setUsernameStatus('checking');
    setUsernameMsg('');

    const timer = setTimeout(() => setDebouncedUsername(trimmed), 2000);
    return () => clearTimeout(timer);
  }, [formData.username, isSignupFlow]);

  const checkUsername = useCallback(async (username: string) => {
    if (!username || username.length < 2) return;
    setUsernameStatus('checking');
    try {
      const { data } = await apiClient.get(
        `/custom_account/username-availability/?username=${encodeURIComponent(username)}`,
      );
      if (data?.available) {
        setUsernameStatus('available');
        setUsernameMsg('Username is available');
      } else {
        setUsernameStatus('taken');
        setUsernameMsg(data?.message ?? 'Username is already taken');
      }
    } catch {
      setUsernameStatus('idle');
    }
  }, []);

  useEffect(() => {
    if (debouncedUsername) checkUsername(debouncedUsername);
  }, [debouncedUsername, checkUsername]);

  useEffect(() => {
    if (isSignupFlow) {
      const draft = signupStore.get();
      setFormData((prev) => ({
        ...prev,
        fullName: draft.fullName || prev.fullName,
        username: draft.username || prev.username,
        dob: draft.dateOfBirth || prev.dob,
        password: draft.password || prev.password,
        confirmPassword: draft.password || prev.confirmPassword,
      }));
      if (draft.profilePicUrl && !imagePreview) {
        setImagePreview(draft.profilePicUrl);
      }

      // Restore manual image if it exists in store
      const manualFile = signupStore.getProfileImage();
      if (manualFile && !imageFile) {
        setImageFile(manualFile);
        const reader = new FileReader();
        reader.onload = () => setImagePreview(reader.result as string);
        reader.readAsDataURL(manualFile);
      }
    }
  }, [isSignupFlow, imagePreview, imageFile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Type validation
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file (JPEG, PNG, etc.)');
      return;
    }

    // Size validation (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setImageFile(file);
    signupStore.setProfileImage(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSignupFlow) {
      if (formData.fullName.trim().length < 2) {
        toast.error('Full name must be at least 2 characters.');
        return;
      }
      if (formData.username.trim().length < 3) {
        toast.error('Username must be at least 3 characters.');
        return;
      }
      if (usernameStatus === 'taken') {
        toast.error(usernameMsg);
        return;
      }
      if (usernameStatus === 'checking') {
        toast.error('Please wait while we check username availability.');
        return;
      }
      if (!formData.dob) {
        toast.error('Date of birth is required.');
        return;
      }

      // Age validation: must be >= 13 years old
      const birthDate = new Date(formData.dob);
      const today = new Date();

      if (isNaN(birthDate.getTime())) {
        toast.error('Please enter a valid date of birth.');
        return;
      }

      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      if (age < 13) {
        toast.error('You must be at least 13 years old to register.');
        return;
      }
      if (!imagePreview) {
        toast.error('Please upload a profile photo.');
        return;
      }

      if (!isSocialSignup) {
        if (!formData.password) {
          toast.error('Password is required.');
          setHasSubmitted(true);
          return;
        }
        if (!passwordIsValid) {
          toast.error('Password does not meet requirements.');
          setHasSubmitted(true);
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match.');
          setHasSubmitted(true);
          return;
        }
      }

      signupStore.set({
        fullName: formData.fullName.trim(),
        username: formData.username.trim(),
        dateOfBirth: formData.dob,
        password: isSocialSignup ? '' : formData.password,
      });
      navigate({ to: '/music-style' });
      return;
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      await profileMutation.mutateAsync({
        full_name: formData.fullName,
        date_of_birth: formData.dob,
        username: formData.username,
        ...(formData.password && { password: formData.password }),
      });
      updateUser({ fullName: formData.fullName, username: formData.username });
      navigate({ to: '/music-style' });
    } catch (error) {
      console.error('API Error:', error);
      updateUser({ fullName: formData.fullName, username: formData.username });
      navigate({ to: '/music-style' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex bg-[#030303] flex-col overflow-hidden font-['Inter'] selection:bg-blue-500/30">
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          width: '1308px',
          height: '449px',
          bottom: '-150px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(to right, #2F80ED, #942FED)',
          opacity: 0.5,
          filter: 'blur(260px)',
          zIndex: 0,
        }}
      />

      <AuthHeader />

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center w-full max-w-200 mx-auto px-6 py-20">
        <div className="w-full flex justify-start mb-4">
          <button
            onClick={() => window.history.back()}
            className="w-12 h-12 rounded-full bg-[#1a1a1a] hover:bg-[#2a2a2a] flex items-center justify-center transition-colors border border-white/5 cursor-pointer shadow-lg"
            aria-label="Go back"
          >
            <FiArrowLeft className="text-zinc-300" size={18} />
          </button>
        </div>

        <div className="w-full text-center mb-8">
          <h1
            className="text-white text-[32px] md:text-[40px] font-medium leading-[1.1] mb-2"
            style={{ fontFamily: "'Funnel Display', sans-serif" }}
          >
            Personal Information
          </h1>
          <p className="text-[#a1a1aa] text-[14px] md:text-[15px] font-medium">
            Let&apos;s get to know you better
          </p>
        </div>

        <div className="w-full max-w-160 flex flex-col gap-6">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handlePhotoSelect}
          />
          <div
            onClick={() => isSignupFlow && fileInputRef.current?.click()}
            className={`w-full bg-[#0a0a0a] border border-white/5 rounded-2xl p-6 flex items-center gap-6 transition-colors group ${isSignupFlow ? 'cursor-pointer hover:bg-[#111111]' : 'cursor-default'}`}
          >
            <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0 border border-blue-500/30 group-hover:bg-blue-500/30 transition-colors overflow-hidden">
              {imagePreview ? (
                <img src={imagePreview} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <FiUser className="text-blue-500" size={32} />
              )}
            </div>
            <div className="flex flex-col">
              <span
                className="text-white text-[16px] font-bold mb-1"
                style={{ fontFamily: "'Satoshi', sans-serif" }}
              >
                Profile Photo{isSignupFlow && <span className="text-red-400 ml-1">*</span>}
              </span>
              <span className="text-[#BEBEBE] text-[13px] flex items-center gap-1.5">
                <FiCamera size={13} /> {imageFile ? imageFile.name : 'Upload a clear profile image'}
              </span>
            </div>
          </div>

          <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <FormLabel className="text-white text-[13px] font-medium ml-1">Full Name</FormLabel>
                <div className="relative flex items-center bg-[#0a0a0a] border border-white/5 rounded-xl h-12 px-4 focus-within:border-blue-500/50 transition-colors">
                  <FiUser className="text-zinc-500 mr-3" size={18} />
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    placeholder="Enter Full Name"
                    className="bg-transparent border-none text-white text-[14px] placeholder:text-zinc-600 w-full focus:outline-none focus:ring-0"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <FormLabel className="text-white text-[13px] font-medium ml-1">
                  Date of Birth
                </FormLabel>
                <DateInput
                  name="dob"
                  value={formData.dob}
                  onChange={(value) =>
                    handleChange({
                      target: { name: 'dob', value },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  icon={FiCalendar}
                  className="h-12 bg-[#0a0a0a] border-white/5"
                  required
                />
              </div>

              <div className="flex flex-col gap-2">
                <FormLabel className="text-white text-[13px] font-medium ml-1">Username</FormLabel>
                <div
                  className={`relative flex items-center bg-[#0a0a0a] border rounded-xl h-12 px-4 focus-within:border-blue-500/50 transition-colors ${usernameStatus === 'taken' ? 'border-red-500/50' : usernameStatus === 'available' ? 'border-green-500/50' : 'border-white/5'}`}
                >
                  <FiMail className="text-zinc-500 mr-3" size={18} />
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    placeholder="e.g. graham_toker"
                    className="bg-transparent border-none text-white text-[14px] placeholder:text-zinc-600 w-full focus:outline-none focus:ring-0"
                  />
                  {usernameStatus === 'checking' && (
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin shrink-0" />
                  )}
                </div>
                {usernameMsg && (
                  <p
                    className={`text-xs ml-1 ${usernameStatus === 'available' ? 'text-green-400' : 'text-red-400'}`}
                  >
                    {usernameMsg}
                  </p>
                )}
              </div>

              {!isSocialSignup && (
                <div className="flex flex-col gap-2">
                  <FormLabel
                    optional={!isSignupFlow}
                    className="text-white text-[13px] font-medium ml-1"
                  >
                    Create Password
                  </FormLabel>
                  <div className="relative flex items-center bg-[#0a0a0a] border border-white/5 rounded-xl h-12 px-4 focus-within:border-blue-500/50 transition-colors">
                    <FiLock className="text-zinc-500 mr-3" size={18} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required={!isSocialSignup}
                      placeholder="Enter your password"
                      className="bg-transparent border-none text-white text-[14px] placeholder:text-zinc-600 flex-1 focus:outline-none focus:ring-0"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer shrink-0 ml-2"
                    >
                      {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  {/* Password strength indicators – signup flow only */}
                  {isSignupFlow && formData.password.length > 0 && (
                    <div className="flex flex-wrap gap-x-3 gap-y-1 ml-1 mt-0.5">
                      {[
                        { label: '8–20 chars', ok: pwStrength.isLengthValid },
                        { label: 'Uppercase', ok: pwStrength.hasUppercase },
                        { label: 'Number', ok: pwStrength.hasNumber },
                        { label: 'Special char', ok: pwStrength.hasSpecialChar },
                      ].map(({ label, ok }) => (
                        <span
                          key={label}
                          className={`text-[11px] font-medium flex items-center gap-1 ${
                            ok ? 'text-green-400' : hasSubmitted ? 'text-red-400' : 'text-zinc-500'
                          }`}
                        >
                          <span>{ok ? '✓' : '○'}</span> {label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {!isSocialSignup && (
              <div className="flex flex-col gap-2">
                <FormLabel
                  optional={!isSignupFlow}
                  className="text-white text-[13px] font-medium ml-1"
                >
                  Confirm Password
                </FormLabel>
                <div className="relative flex items-center bg-[#0a0a0a] border border-white/5 rounded-xl h-12 px-4 focus-within:border-blue-500/50 transition-colors w-full">
                  <FiLock className="text-zinc-500 mr-3" size={18} />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required={!isSocialSignup}
                    placeholder="Confirm your password"
                    className="bg-transparent border-none text-white text-[14px] placeholder:text-zinc-600 flex-1 focus:outline-none focus:ring-0"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer shrink-0 ml-2"
                  >
                    {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
                {formData.confirmPassword.length > 0 && (
                  <div className="ml-1 mt-0.5">
                    <span
                      className={`text-[11px] font-medium flex items-center gap-1 ${
                        formData.password === formData.confirmPassword
                          ? 'text-green-400'
                          : hasSubmitted
                            ? 'text-red-400'
                            : 'text-zinc-500'
                      }`}
                    >
                      <span>{formData.password === formData.confirmPassword ? '✓' : '○'}</span>{' '}
                      Passwords match
                    </span>
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="mt-4 h-12 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-medium text-[15px] rounded-xl transition-colors shadow-lg shadow-blue-500/20 active:scale-[0.98] cursor-pointer disabled:opacity-50"
            >
              {isLoading ? 'Saving…' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
